import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArticleModal } from './ArticleModal';
import '../styles/NewsPage.css';

const API_BASE = '/admin/news/api/get-articles.php';
const SKY_NEWS_API_BASE = 'https://news-rss-api.onoradio-news.workers.dev';

export const NewsPage = () => {
  const { slug: urlSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [newsType, setNewsType] = useState('utter'); // 'utter' or 'sky'
  const [newsData, setNewsData] = useState({
    allArticles: [],
    articlesByCategory: {},
    trending: []
  });

  const [activeCategory, setActiveCategory] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);

  const articlesPerPage = 6;

  const UTTER_CATEGORIES = ['All', 'News', 'Sport'];
  const SKY_CATEGORIES = [
    'All',
    'General',
    'UK',
    'Technology',
    'Politics',
    'Business',
    'Entertainment'
  ];

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // Determine if URL slug is a category or article slug
  useEffect(() => {
    if (!urlSlug) {
      setActiveCategory('All');
      setSelectedArticle(null);
      return;
    }

    // Check if the slug matches a category for current news type
    const currentCategories = newsType === 'utter' ? UTTER_CATEGORIES : SKY_CATEGORIES;
    const matchingCategory = currentCategories.find(
      c => c.toLowerCase() === urlSlug.toLowerCase()
    );

    if (matchingCategory) {
      setActiveCategory(matchingCategory);
      setSelectedArticle(null);
    } else {
      // Treat as article slug
      setActiveCategory('All');
      setSelectedArticle(urlSlug);
    }
  }, [urlSlug, newsType]);

  // Fetch Utter News
  const fetchUtterNews = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const categoriesToFetch =
        activeCategory === 'All'
          ? UTTER_CATEGORIES.filter(c => c !== 'All')
          : [activeCategory];

      const results = await Promise.all(
        categoriesToFetch.map(async (category) => {
          const url = `${API_BASE}?category=${encodeURIComponent(category)}`;
          const res = await fetch(url);
          if (!res.ok) throw new Error(`Failed to fetch ${category}`);
          const data = await res.json();
          return { category, data };
        })
      );

      const articlesByCategory = {};
      const allArticlesMap = new Map();

      results.forEach(({ category, data }) => {
        const normalized = data.map((a) => ({
          id: a.id,
          title: a.title,
          slug: a.slug,
          excerpt: a.description,
          link: a.link,
          image: a.image,
          category,
          date: a.date,
          pubDate: new Date(a.date).getTime(),
          source: a.source,
          author: a.author || 'Editorial Team'
        }));

        articlesByCategory[category.toLowerCase()] = normalized;

        normalized.forEach(article => {
          if (!allArticlesMap.has(article.id)) {
            allArticlesMap.set(article.id, article);
          }
        });
      });

      const allArticles = Array.from(allArticlesMap.values());
      allArticles.sort((a, b) => b.pubDate - a.pubDate);

      setNewsData({
        allArticles,
        articlesByCategory,
        trending: allArticles.slice(0, 5)
      });

    } catch (err) {
      console.error('Utter news fetch error:', err);
      setError('Failed to load news. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch Sky News
  const fetchSkyNews = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const categoriesToFetch =
        activeCategory === 'All'
          ? SKY_CATEGORIES.filter(c => c !== 'All')
          : [activeCategory];

      const results = await Promise.all(
        categoriesToFetch.map(async (category) => {
          const url = `${SKY_NEWS_API_BASE}?category=${encodeURIComponent(category)}`;
          const res = await fetch(url);
          if (!res.ok) throw new Error('Fetch failed');
          const data = await res.json();
          return { category, data };
        })
      );

      const articlesByCategory = {};
      const allArticlesMap = new Map();

      results.forEach(({ category, data }) => {
        const normalized = data.map((a) => {
          const id = a.link;
          return {
            id,
            title: a.title,
            slug: a.slug || a.link.split('/').pop(),
            excerpt: a.description,
            link: a.link,
            image: a.image,
            category,
            date: a.date,
            pubDate: new Date(a.date).getTime(),
            source: a.source
          };
        });

        articlesByCategory[category.toLowerCase()] = normalized;

        normalized.forEach(article => {
          if (!allArticlesMap.has(article.link)) {
            allArticlesMap.set(article.link, article);
          }
        });
      });

      const allArticles = Array.from(allArticlesMap.values());
      allArticles.sort((a, b) => b.pubDate - a.pubDate);

      setNewsData({
        allArticles,
        articlesByCategory,
        trending: allArticles.slice(0, 5)
      });

    } catch (err) {
      console.error('Sky News fetch error:', err);
      setError('Failed to load Sky News. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  // Main fetch function
  const fetchNews = async () => {
    if (newsType === 'utter') {
      await fetchUtterNews();
    } else {
      await fetchSkyNews();
    }
  };

  useEffect(() => {
    fetchNews();
    setCurrentPage(1);
  }, [activeCategory, newsType]);

  const handleCategoryChange = (category) => {
    setActiveCategory(category);
    setSelectedArticle(null);
    navigate(
      category === 'All' ? '/news' : `/news/${category.toLowerCase()}`
    );
  };

  const handleNewsTypeChange = (type) => {
    setNewsType(type);
    setActiveCategory('All');
    setSelectedArticle(null);
    navigate('/news');
  };

  const handleArticleClick = (article, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    console.log('Article clicked:', article);
    console.log('Article slug:', article.slug);
    
    // Set the selected article first
    setSelectedArticle(article.slug);
    
    // Then navigate
    navigate(`/news/${article.slug}`);
    
    // Force scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCloseModal = () => {
    setSelectedArticle(null);
    navigate(activeCategory === 'All' ? '/news' : `/news/${activeCategory.toLowerCase()}`);
  };

  const getFilteredArticles = () => {
    if (activeCategory === 'All') return newsData.allArticles;
    return newsData.articlesByCategory[activeCategory.toLowerCase()] || [];
  };

  const filteredArticles = getFilteredArticles();
  const indexOfLast = currentPage * articlesPerPage;
  const indexOfFirst = indexOfLast - articlesPerPage;
  const currentArticles = filteredArticles.slice(indexOfFirst, indexOfLast);

  const totalPages = Math.ceil(filteredArticles.length / articlesPerPage);

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const currentCategories = newsType === 'utter' ? UTTER_CATEGORIES : SKY_CATEGORIES;

  return (
    <div className="pageContainer">
      {/* Article Modal */}
      <ArticleModal 
        isOpen={!!selectedArticle}
        onClose={handleCloseModal}
        articleSlug={selectedArticle}
        type="news"
      />

      <section className="news-page-header">
        <h1>
          {newsType === 'utter' ? 'Utter News' : 'Sky News'}
        </h1>
        <p>
          {newsType === 'utter' 
            ? 'Read all the latest news from Utter Radio.' 
            : 'Latest headlines from Sky News.'}
        </p>

        <div className="news-header-controls">
          <div className="news-type-toggle">
            <button
              className={`toggle-btn ${newsType === 'utter' ? 'active' : ''}`}
              onClick={() => handleNewsTypeChange('utter')}
            >
              <span>Utter News</span>
            </button>
            <button
              className={`toggle-btn ${newsType === 'sky' ? 'active' : ''}`}
              onClick={() => handleNewsTypeChange('sky')}
            >
              <span>Sky News</span>
            </button>
          </div>

          <button
            className="btn btn-refresh"
            onClick={fetchNews}
            disabled={isLoading}
          >
            <i className={`fas ${isLoading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} />
            {isLoading ? ' Loading...' : ' Refresh'}
          </button>
        </div>
      </section>

      <div className="news-categories">
        {currentCategories.map(cat => (
          <button
            key={cat}
            className={`category-btn ${activeCategory === cat ? 'active' : ''}`}
            onClick={() => handleCategoryChange(cat)}
          >
            {cat}
          </button>
        ))}
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="loading-state">
          <div className="spinner" />
          <p>Loading {newsType === 'utter' ? 'news' : 'Sky News'}…</p>
        </div>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <div className="error-state" style={{
          textAlign: 'center',
          padding: '60px 20px',
          background: 'linear-gradient(140deg,rgba(190, 7, 64, 1) 0%, rgba(153, 5, 52, 1) 50%, rgba(237, 55, 112, 1) 100%)',
          margin: '20px 0'
        }}>
          <i className="fas fa-exclamation-triangle" style={{ fontSize: '48px', color: '#fff', marginBottom: '20px' }}></i>
          <p style={{ color: '#fff', fontSize: '18px', marginBottom: '20px' }}>{error}</p>
          <button 
            className="btn btn-refresh" 
            onClick={fetchNews}
            style={{
              padding: '12px 24px',
              background: '#fff',
              color: '#333',
              border: 'none',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            <i className="fas fa-sync-alt"></i> Try Again
          </button>
        </div>
      )}

      {/* Empty State - No Articles Found */}
      {!isLoading && !error && filteredArticles.length === 0 && (
        <div className="empty-state" style={{
          textAlign: 'center',
          padding: '60px 20px',
          background: '#fff',
          margin: '20px 0',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <i className="fas fa-newspaper" style={{ fontSize: '64px', color: '#9ca3af', marginBottom: '20px' }}></i>
          <h3 style={{ color: '#374151', marginBottom: '10px', fontSize: '24px' }}>
            No Articles Found
          </h3>
          <p style={{ color: '#6b7280', marginBottom: '20px', fontSize: '16px' }}>
            {activeCategory === 'All' 
              ? 'There are no articles available at the moment. Check back soon for the latest news!' 
              : `No articles found in the ${activeCategory} category. Try selecting a different category.`}
          </p>
          <button 
            className="btn btn-refresh" 
            onClick={fetchNews}
            style={{
              padding: '12px 24px',
              background: '#BD003A',
              color: 'white',
              border: 'none',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            <i className="fas fa-sync-alt"></i> Refresh
          </button>
        </div>
      )}

      {/* Articles Grid - No featured article at top */}
      {!isLoading && !error && filteredArticles.length > 0 && (
        <div className="news-grid">
          <div className="featured-news">
            <div className="news-articles-grid">
              {currentArticles.map(article => (
                <article key={article.id} className="news-article-card">
                  <img src={article.image} alt={article.title} />
                  <div className="article-content">
                    <span className={`news-category ${newsType === 'utter' ? 'utter-category' : ''}`}>
                      {article.category}
                    </span>
                    {newsType === 'utter' && article.author && (
                      <span className="article-author">By {article.author}</span>
                    )}
                    <h3>{article.title}</h3>
                    <p>{article.excerpt}</p>
                    <div className="article-meta">
                      <span><i className="fas fa-calendar"></i> {formatDate(article.date)}</span>
                    </div>
                    {newsType === 'utter' ? (
                      <button
                        onClick={(e) => handleArticleClick(article, e)}
                        className="btn btn-outline"
                        style={{ cursor: 'pointer' }}
                      >
                        Read More
                      </button>
                    ) : (
                      <a
                        href={article.link}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-outline"
                      >
                        Read More
                      </a>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </div>

          <aside className="news-sidebar">
            <h3>
              <i className={`fas ${newsType === 'utter' ? 'fa-star' : 'fa-fire'}`} />
              {newsType === 'utter' ? " Editor's Picks" : ' Trending'}
            </h3>
            {newsData.trending.length > 0 ? (
              <ul className="trending-news">
                {newsData.trending.map((t, i) => (
                  <li key={t.id}>
                    {newsType === 'utter' ? (
                      <button 
                        onClick={(e) => handleArticleClick(t, e)}
                        style={{ 
                          background: 'none', 
                          border: 'none', 
                          cursor: 'pointer',
                          textAlign: 'left',
                          padding: 0,
                          color: 'inherit',
                          font: 'inherit'
                        }}
                      >
                        <span>{i + 1}</span>
                        {t.title}
                      </button>
                    ) : (
                      <a 
                        href={t.link} 
                        target="_blank" 
                        rel="noreferrer"
                      >
                        <span>{i + 1}</span>
                        {t.title}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p style={{ color: '#6b7280', textAlign: 'center', padding: '20px' }}>
                No trending articles yet
              </p>
            )}
          </aside>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="page-btn"
            disabled={currentPage === 1}
            onClick={() => goToPage(currentPage - 1)}
          >
            ‹ Prev
          </button>
          {[...Array(totalPages)].map((_, i) => {
            const page = i + 1;
            return (
              <button
                key={page}
                className={`page-btn ${page === currentPage ? 'active' : ''}`}
                onClick={() => goToPage(page)}
              >
                {page}
              </button>
            );
          })}
          <button
            className="page-btn"
            disabled={currentPage === totalPages}
            onClick={() => goToPage(currentPage + 1)}
          >
            Next ›
          </button>
        </div>
      )}
    </div>
  );
};